import React, { useCallback, useMemo, useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import currencyFormatter from 'currency-formatter';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import StarRateIcon from '@mui/icons-material/StarRate';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';
import { ONBOARD_CREDIT_ICON } from '~/components/Cruises/pages/Inclusions/InclusionsEdit/InclusionsItem/components/InclusionItemForm';
import { CruiseInclusionItem } from '~/components/Cruises/pages/Inclusions/types';

import inclusionsService from '~/services/cruises/InclusionsService';

const INCLUSION_ITEM_TYPE = {
  ONBOARD_CREDIT: 'Onboard Credit',
  FREE_TEXT: 'Free Text',
};

function formatCategory(category: string): string {
  return category
    .split('_')
    .map((word) => `${word.charAt(0)}${word.slice(1).toLowerCase()}`)
    .join(' ');
}

interface Props {
  inclusionItem: CruiseInclusionItem;
  isHighlightable: boolean;
  showDisplayType: boolean;
  onChange: () => void;
}

function InclusionsItemListItem({ inclusionItem, isHighlightable, showDisplayType, onChange }: Props) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: inclusionItem.id,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { inclusionId } = useParams<{ inclusionId: string }>();
  const [isUpdating, setIsUpdating] = useState(false);

  const showConfirmDialog = useConfirmDialog();

  const handleDeleteRecord = useCallback(async () => {
    try {
      const confirmed = await showConfirmDialog({
        title: 'Delete Inclusion?',
        description: `This will delete the inclusion, are you sure you want to proceed?`,
      });

      if (!confirmed) {
        return;
      }

      await inclusionsService.deleteItem(inclusionId, inclusionItem.id);
      enqueueSnackbar('Inclusion deleted', { variant: 'success' });
      onChange();
    } catch (error) {
      enqueueSnackbar('Error deleting Inclusion', { variant: 'error' });
    }
  }, [enqueueSnackbar, inclusionId, inclusionItem.id, showConfirmDialog]);

  const handleHighlightInclusion = useCallback(async () => {
    try {
      setIsUpdating(true);

      const isHighlighted = !inclusionItem.isHighlighted;
      await inclusionsService.updateItem(inclusionId, inclusionItem.id, {
        description: inclusionItem.description,
        type: inclusionItem.type,
        creditCurrency: inclusionItem.creditCurrency,
        creditAmount: inclusionItem.creditAmount,
        icon: inclusionItem.icon ?? (inclusionItem.type === 'ONBOARD_CREDIT' ? ONBOARD_CREDIT_ICON : undefined),
        isHighlighted,
      });
      enqueueSnackbar(isHighlighted ? 'Inclusion highlighted' : 'Inclusion removed from highlight', {
        variant: 'success',
      });
      onChange();
    } catch (error) {
      enqueueSnackbar('Error highlighting Inclusion', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  }, [enqueueSnackbar, inclusionId, inclusionItem.id, inclusionItem.isHighlighted]);

  const description = useMemo(() => {
    if (inclusionItem.type === 'ONBOARD_CREDIT' && inclusionItem.creditAmount) {
      return currencyFormatter.format(inclusionItem.creditAmount, {
        code: inclusionItem.creditCurrency,
      });
    }

    return inclusionItem.description;
  }, [inclusionItem]);

  return (
    <ListItem
      key={inclusionItem.id}
      {...attributes}
      sx={{
        flexGrow: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box ref={setNodeRef} {...listeners} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }} mr={2}>
        <DragIndicatorIcon />
      </Box>

      <ListItemText
        primary={description}
        secondary={
          <>
            <Box component="span" display="block">
              {`Type: ${INCLUSION_ITEM_TYPE[inclusionItem.type]} | Icon: ${
                inclusionItem.icon ?? (inclusionItem.type === 'ONBOARD_CREDIT' ? ONBOARD_CREDIT_ICON : '-')
              }`}
            </Box>
            {(!!inclusionItem.categories?.length || (showDisplayType && !!inclusionItem.displayType)) && (
              <Box mt={0.5} display="flex" gap={0.5} flexWrap="wrap">
                {showDisplayType && inclusionItem.displayType && (
                  <Chip label={formatCategory(inclusionItem.displayType)} size="small" variant="outlined" />
                )}
                {(inclusionItem.categories ?? []).map((category) => (
                  <Chip key={category} label={formatCategory(category)} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </>
        }
      />

      {isHighlightable && (
        <Tooltip
          title={inclusionItem.isHighlighted ? 'Remove highlight' : 'Display inclusion highlighted on the deal page'}
        >
          <Button variant="text" size="small" color="info" onClick={handleHighlightInclusion} disabled={isUpdating}>
            {isUpdating ? <Spinner size={16} /> : inclusionItem.isHighlighted ? <StarRateIcon /> : <StarOutlineIcon />}
          </Button>
        </Tooltip>
      )}

      <Tooltip title="Edit inclusion">
        <Button
          component={Link}
          to={`/cruises/inclusions/edit/${inclusionId}/items/edit/${inclusionItem.id}`}
          variant="text"
          size="small"
        >
          <EditIcon />
        </Button>
      </Tooltip>

      <Tooltip title="Delete inclusion">
        <Button variant="text" size="small" color="error" onClick={handleDeleteRecord}>
          <DeleteIcon />
        </Button>
      </Tooltip>
    </ListItem>
  );
}

export default InclusionsItemListItem;
